import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Draggable } from 'gsap/Draggable';
import { split, stepWord, renderWord, atRest, type Word } from './springText';
import { initTextMotion } from './textMotion';
import { initLabArchive } from './labArchive';
import { initSkiper72 } from './skiper72';

gsap.registerPlugin(ScrollTrigger, Draggable);

// Reusable text/card components, used on the home page and every inner page. Each one looks for its own markup
// and does nothing if it isn't on the page. Transforms and opacity only; one shared ticker.
const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function initComponents(): void {
  initSpringHeadings();
  initCounters();
  initCycleWords();
  initStack();
  initOrbit();
  initLabAccordion();
  initDeck();
  initTeamLineup();
  initFilter();
  initInlineVideos();
  initLabArchive();
  initTextMotion();
  initSkiper72();
}

// Alloy-inspired covers: fixed-size content, moving panels and scaled backplates.
// Layout is measured only on resize; opening a card animates transforms and opacity.
function initLabAccordion(): void {
  document.querySelectorAll<HTMLElement>('.lab-accordion').forEach((root) => {
    const cards = [...root.querySelectorAll<HTMLElement>('.lab-panel')];
    const buttons = cards.map((c) => c.querySelector<HTMLButtonElement>('.lab-trigger')!);
    const details = cards.map((c) => c.querySelector<HTMLElement>('.lab-detail')!);
    const surfaces = cards.map((c) => c.querySelector<HTMLElement>('.lab-surface')!);
    const peeks = cards.map((c) => c.querySelector<HTMLElement>('.lab-peek')!);
    let active = 0, vertical = false, rail = 92, extent = 0, height = 0;
    let motion: gsap.core.Timeline | undefined;
    root.classList.add('is-ready');
    buttons.forEach((b) => { b.disabled = false; });

    const show = (index: number, animate = true) => {
      active = index;
      motion?.kill();
      const duration = animate && !reduce() ? 0.65 : 0;
      motion = gsap.timeline({ defaults: { duration, ease: 'sine.inOut', overwrite: 'auto' } });
      let offset = 0;
      cards.forEach((card, i) => {
        const open = i === active;
        buttons[i].setAttribute('aria-expanded', String(open));
        buttons[i].setAttribute('aria-disabled', String(open)); // one panel always stays open
        details[i].inert = !open;
        details[i].setAttribute('aria-hidden', String(!open));
        card.classList.toggle('is-open', open);
        motion!.to(card, { x: vertical ? 0 : offset, y: vertical ? offset : 0 }, 0);
        motion!.to(surfaces[i], { scaleX: vertical || open ? 1 : rail / extent,
          scaleY: !vertical || open ? 1 : rail / height }, 0);
        motion!.to(peeks[i], { opacity: open ? 0 : 1, duration: duration * 0.4 }, 0);
        motion!.to(buttons[i].querySelector('.lab-toggle'), { rotation: open ? 45 : 0 }, 0);
        motion!.to(details[i], { opacity: open ? 1 : 0, x: open || vertical ? 0 : 16,
          y: open || !vertical ? 0 : 12, duration: duration * (open ? 0.5 : 0.2) }, open ? duration * 0.5 : 0);
        offset += (open ? (vertical ? height : extent) : rail) + 12;
      });
    };
    const measure = () => {
      const width = root.clientWidth;
      vertical = width < 850;
      rail = 76;
      extent = vertical ? width : width - (cards.length - 1) * (rail + 12);
      root.classList.toggle('is-vertical', vertical);
      root.style.setProperty('--lab-rail', `${rail}px`);
      cards.forEach((c) => { c.style.width = `${extent}px`; });
      height = Math.max(...details.map((d) => d.scrollHeight));
      height = Math.max(height, vertical ? 520 : 580);
      cards.forEach((c) => { c.style.height = `${height}px`; });
      root.style.height = `${vertical ? height + (cards.length - 1) * (rail + 12) : height}px`;
      show(active, false);
      ScrollTrigger.refresh();
    };
    buttons.forEach((button, i) => {
      button.addEventListener('click', (event) => { if (active !== i) show(i, event.detail !== 0); });
      button.addEventListener('keydown', (event) => {
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1
          : ['ArrowRight', 'ArrowDown'].includes(event.key) ? (i + 1) % cards.length
          : ['ArrowLeft', 'ArrowUp'].includes(event.key) ? (i + cards.length - 1) % cards.length : -1;
        if (next < 0) return;
        event.preventDefault(); buttons[next].focus(); show(next, false);
      });
    });
    let lastWidth = 0;
    new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === lastWidth) return;
      lastWidth = entry.contentRect.width; measure();
    }).observe(root);
    document.fonts.ready.then(measure);
    measure();
  });
}

// ── [data-spring] headings: letters ripple up when they enter, wobble when the cursor pokes them ──
function initSpringHeadings(): void {
  const els = [...document.querySelectorAll<HTMLElement>('[data-spring]')];
  if (!els.length) return;
  const words: { w: Word; on: boolean; go: boolean }[] = els.map((el) => ({ w: split(el), on: false, go: false }));
  if (reduce()) return;
  words.forEach((item) => {
    // Wait below the line, invisible; on enter fade in and let the springs pull each letter up (staggered by height)
    item.w.letters.forEach((l, i) => { l.y = Math.min(24 + i * 3, 70); });
    renderWord(item.w);
    item.w.el.style.opacity = '0';
    ScrollTrigger.create({
      trigger: item.w.el, start: 'top 90%', once: true,
      onEnter: () => { item.go = true; gsap.to(item.w.el, { opacity: 1, duration: 0.5, ease: 'power2.out' }); },
    });
    new IntersectionObserver(([e]) => { item.on = e.isIntersecting; }).observe(item.w.el);
  });
  gsap.ticker.add((_t, ms) => {
    const dt = Math.min(ms, 50) / 2000;
    words.forEach(({ w, on, go }) => {
      if (!on || !go || atRest(w)) return;
      stepWord(w, dt); stepWord(w, dt);
      renderWord(w);
    });
  });
}

// ── <span class="count" data-count="2025">: odometer, each digit rolls to place on enter ──
function initCounters(): void {
  document.querySelectorAll<HTMLElement>('[data-count]').forEach((el) => {
    const digits = (el.dataset.count ?? el.textContent ?? '').split('');
    el.setAttribute('aria-label', digits.join(''));
    el.textContent = '';
    const cols = digits.map((d) => {
      const col = document.createElement('span');
      col.className = 'count-col';
      col.setAttribute('aria-hidden', 'true');
      if (!/\d/.test(d)) { col.textContent = d; el.append(col); return null; }
      const reel = document.createElement('span');
      reel.className = 'count-reel';
      reel.innerHTML = '0123456789'.split('').map((n) => `<span>${n}</span>`).join('');
      col.append(reel); el.append(col);
      return { reel, d: +d };
    });
    const go = () => cols.forEach((c, i) => c && gsap.to(c.reel, { yPercent: -10 * c.d, duration: 1.4 + i * 0.15, ease: 'power3.inOut' }));
    if (reduce()) cols.forEach((c) => c && gsap.set(c.reel, { yPercent: -10 * c.d }));
    else ScrollTrigger.create({ trigger: el, start: 'top 90%', once: true, onEnter: go });
  });
}

// ── <span data-cycle="Build.|Grow.|Scale.">: words roll through, one at a time ──
function initCycleWords(): void {
  document.querySelectorAll<HTMLElement>('[data-cycle]').forEach((el) => {
    const words = (el.dataset.cycle ?? '').split('|');
    el.setAttribute('aria-label', words.join(' '));
    el.innerHTML = words.map((w) => `<span aria-hidden="true">${w}</span>`).join('');
    const spans = [...el.children] as HTMLElement[];
    gsap.set(spans, { yPercent: 110 });
    gsap.set(spans[0], { yPercent: 0 });
    if (reduce() || spans.length < 2) return;
    const tl = gsap.timeline({ repeat: -1 });
    spans.forEach((s, i) => {
      const next = spans[(i + 1) % spans.length];
      tl.to(s, { yPercent: -110, duration: 0.7, ease: 'power3.inOut' }, '+=1.6')
        .fromTo(next, { yPercent: 110 }, { yPercent: 0, duration: 0.7, ease: 'power3.inOut' }, '<');
    });
  });
}

// ── .stack > .stack-card: a pinned deck. The front card tilts toward you and lifts away, the next one grows into
// its place; the cards behind peek out below, each a step smaller. Without JS / with reduced motion it stays a list ──
function initStack(): void {
  const STEP = 0.06, DROP = 18, SHOWN = 3; // per card behind: 6% smaller, 18px lower; 3 peek out
  const rest = (d: number) => ({ scale: 1 - d * STEP, y: d * DROP, opacity: d > SHOWN ? 0 : 1 });
  document.querySelectorAll<HTMLElement>('.stack').forEach((stack) => {
    const cards = [...stack.querySelectorAll<HTMLElement>('.stack-card')];
    const section = stack.closest<HTMLElement>('section') ?? stack;
    if (reduce() || cards.length < 2) return;
    stack.classList.add('is-deck');
    stack.style.setProperty('--peek', `${SHOWN * DROP}px`);
    cards.forEach((c, i) => {
      c.style.zIndex = String(cards.length - i);
      gsap.set(c, { ...rest(i), transformOrigin: '50% 100%', transformPerspective: 1400 });
    });
    const tl = gsap.timeline({
      defaults: { ease: 'power2.inOut', duration: 1 },
      scrollTrigger: {
        trigger: section, pin: true, scrub: 1, invalidateOnRefresh: true,
        start: () => (section.offsetHeight < innerHeight ? 'center center' : 'top top'),
        end: () => `+=${(cards.length - 1) * innerHeight * 1.1}`, // a step is 1.1 screens of scroll for under one screen of lift
      },
    });
    cards.slice(0, -1).forEach((c, k) => {
      // opaque all the way up (a half-faded card ghosts over the next one's text), clear of the screen, then gone
      tl.to(c, { y: () => -(innerHeight / 2 + c.offsetHeight), rotationX: -32, scale: 1.04 }, k)
        .to(c, { opacity: 0, ease: 'none', duration: 0.15 }, k + 0.85);
      cards.slice(k + 1).forEach((next, j) => tl.to(next, rest(j), k));
    });
  });
}

// ── .process-grid: on desktop the section pins and its title moves into the middle. One after another the steps
// come out from behind it and sweep round a 3D orbit (back, right side, front: farther = smaller, dimmer, higher)
// to the point on the front arc above their own column; step 1 goes first and lands leftmost, so no step crosses
// one that has landed. Then the title returns and the steps settle into their row. Tablet, phone and reduced motion
// keep the grid ──
function initOrbit(): void {
  const STAGGER = 0.16, SETTLE = 0.72; // each step leaves 16% of the orbit phase after the last; settle from 72%
  const mm = gsap.matchMedia();
  document.querySelectorAll<HTMLElement>('.process-grid').forEach((grid) => {
    const section = grid.closest<HTMLElement>('section');
    const title = section?.querySelector<HTMLElement>('.section-title');
    const label = section?.querySelector<HTMLElement>('.section-label'); // rides above the title
    const cards = [...grid.querySelectorAll<HTMLElement>('.process-step')];
    if (!section || !title || cards.length < 2) return;
    mm.add('(min-width: 1025px) and (prefers-reduced-motion: no-preference)', () => {
      grid.classList.add('is-orbit');
      cards.forEach((c) => c.classList.remove('reveal')); // its CSS transform transition would fight the scrub
      label?.classList.remove('reveal');
      const heads = label ? [label, title] : [title];
      gsap.set(heads, { position: 'relative', zIndex: 10 });
      let home: { x: number; y: number; end: number }[] = [], cx = 0, cy = 0, rx = 0, ry = 0, tx = 0, ty = 0;
      const measure = () => {
        cx = grid.offsetWidth / 2; cy = grid.offsetHeight / 2;
        rx = grid.offsetWidth * 0.44; ry = grid.offsetHeight * 0.18;
        home = cards.map((c) => {
          const x = c.offsetLeft + c.offsetWidth / 2;
          return { x, y: c.offsetTop + c.offsetHeight / 2, end: Math.asin(gsap.utils.clamp(-0.95, 0.95, (x - cx) / rx)) };
        });
        const range = document.createRange(); range.selectNodeContents(title);
        const t = range.getBoundingClientRect(), g = grid.getBoundingClientRect();
        tx = g.left + cx - (t.left + t.width / 2 - (gsap.getProperty(title, 'x') as number));
        ty = g.top + cy - (t.top + t.height / 2 - (gsap.getProperty(title, 'y') as number));
      };
      const state = { p: 0 };
      const apply = () => {
        const p = state.p, ease = gsap.parseEase('sine.inOut'), clamp = gsap.utils.clamp(0, 1);
        const b = ease(clamp((p - SETTLE) / (1 - SETTLE)));
        const lerp = (a: number, z: number) => a + (z - a) * b;
        const o = clamp(p / SETTLE), n = cards.length;
        cards.forEach((c, i) => {
          const l = clamp((o - i * STAGGER) / (1 - (n - 1) * STAGGER)); // this step's own 0…1 trip
          const a = Math.PI + (home[i].end - Math.PI) * ease(l); // from straight behind, round the right side
          const depth = (Math.cos(a) + 1) / 2; // 1 = front
          gsap.set(c, {
            x: lerp(cx + rx * Math.sin(a) - home[i].x, 0),
            y: lerp(cy + ry * Math.cos(a) - home[i].y, 0), // the far side of the orbit sits higher
            scale: lerp(0.62 + 0.38 * depth, 1),
            opacity: lerp((0.3 + 0.7 * depth) * ease(clamp(l / 0.2)), 1), // out of nowhere behind the title
            rotationY: lerp(-28 * Math.sin(a), 0), transformPerspective: 1200,
            zIndex: Math.round(depth * 20),
          });
        });
        const go = ease(clamp(p / 0.12)); // heads travel to the middle over the orbit's first 12%, so nothing waits in an empty frame
        gsap.set(heads, { x: lerp(tx * go, 0), y: lerp(ty * go, 0) });
      };
      measure(); apply();
      gsap.to(state, {
        p: 1, ease: 'none', onUpdate: apply,
        scrollTrigger: {
          trigger: section, start: 'top top', end: '+=350%', pin: section, scrub: 1,
          onRefresh: () => { measure(); apply(); },
        },
      });
      return () => {
        grid.classList.remove('is-orbit');
        cards.forEach((c) => { c.classList.add('reveal', 'in'); gsap.set(c, { clearProps: 'transform,opacity,zIndex' }); });
        label?.classList.add('reveal', 'in');
        gsap.set(heads, { clearProps: 'transform,position,zIndex' });
      };
    });
  });
}

// ── [data-deck]: a deck of team cards. Drag the top card away (it goes to the back), tap to flip ──
function initDeck(): void {
  document.querySelectorAll<HTMLElement>('[data-deck]').forEach((deck) => {
    const cards = () => [...deck.querySelectorAll<HTMLElement>('.deck-card')]; // last child = top card
    const lay = (animate: boolean) => cards().forEach((c, i, all) => {
      const depth = all.length - 1 - i; // 0 = top
      const v = { x: depth * 10, y: depth * 12, rotation: depth * 4 - 3, scale: 1 - depth * 0.04 };
      c.style.zIndex = String(i);
      c.toggleAttribute('inert', depth > 0);
      animate ? gsap.to(c, { ...v, duration: 0.6, ease: 'back.out(1.6)' }) : gsap.set(c, v);
    });
    const sendBack = (c: HTMLElement, dir = 1) => {
      gsap.to(c, {
        x: dir * deck.offsetWidth * 0.9, rotation: dir * 18, duration: 0.35, ease: 'power2.in',
        onComplete: () => { c.classList.remove('flipped'); deck.prepend(c); lay(true); bind(); },
      });
    };
    let drag: Draggable[] = [];
    const bind = () => {
      drag.forEach((d) => d.kill());
      const top = cards().at(-1);
      if (!top) return;
      drag = Draggable.create(top, {
        type: 'x,y', zIndexBoost: false,
        onDrag() { gsap.set(top, { rotation: this.x / 14 }); },
        onRelease() {
          if (Math.abs(this.x) > deck.offsetWidth * 0.3) sendBack(top, Math.sign(this.x));
          else gsap.to(top, { x: 0, y: 0, rotation: -3, duration: 0.6, ease: 'back.out(1.8)' });
        },
        onClick() { top.classList.toggle('flipped'); },
      });
    };
    lay(false);
    if (!reduce()) bind();
    deck.addEventListener('keydown', (e) => {
      const top = cards().at(-1);
      if (!top || e.target !== top) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); top.classList.toggle('flipped'); }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') sendBack(top, e.key === 'ArrowRight' ? 1 : -1);
    });
    deck.parentElement?.querySelector('[data-deck-next]')?.addEventListener('click', () => {
      const top = cards().at(-1);
      if (top) sendBack(top);
    });
  });
}

// ── [data-team-lineup]: interactive team lineup with bursting props & profile badges ──
function initTeamLineup(): void {
  const root = document.querySelector<HTMLElement>('[data-team-lineup]');
  if (!root) return;

  const backdrop = root.querySelector<HTMLElement>('.team-lineup-backdrop');
  const scrollWrap = root.querySelector<HTMLElement>('.team-lineup-scroll');
  const stage = root.querySelector<HTMLElement>('.team-lineup-stage');
  // Fetch + decode the cast and props a screen and a half early, off the main thread: no hitch on arrival
  const warm = new IntersectionObserver(([e]) => {
    if (!e.isIntersecting) return;
    warm.disconnect();
    root.querySelectorAll('img').forEach((i) => { i.loading = 'eager'; i.decode().catch(() => {}); });
  }, { rootMargin: '150% 0px' });
  warm.observe(root);
  const members = [...root.querySelectorAll<HTMLElement>('.team-member')];
  if (!members.length) return;

  let activeIndex: number | null = null;
  let isTransitioning = false;

  const centerMember = (index: number) => {
    if (!scrollWrap || !stage) return;
    const member = members[index];
    const memberLeft = member.offsetLeft;
    const memberWidth = member.offsetWidth;
    const wrapWidth = scrollWrap.clientWidth;
    const targetScroll = memberLeft - (wrapWidth / 2) + (memberWidth / 2);
    gsap.to(scrollWrap, {
      scrollLeft: Math.max(0, targetScroll),
      duration: 0.5,
      ease: 'power2.out',
    });
  };

  const expandMember = (index: number) => {
    if (isTransitioning) return;
    if (activeIndex === index) {
      collapse();
      return;
    }

    const prevIndex = activeIndex;
    activeIndex = index;
    isTransitioning = true;

    root.classList.add('is-expanded');

    centerMember(index);

    // If another member was previously expanded, reset their props cleanly
    if (prevIndex !== null && members[prevIndex]) {
      const prevProps = [...members[prevIndex].querySelectorAll<HTMLElement>('.team-prop')];
      const prevBadge = members[prevIndex].querySelector<HTMLElement>('.team-badge');
      if (prevProps.length) gsap.killTweensOf(prevProps);
      if (prevBadge) gsap.killTweensOf(prevBadge);
      gsap.set(prevProps, { opacity: 0, scale: 0 });
      gsap.set(prevBadge, { opacity: 0, y: 15 });
    }

    members.forEach((m, i) => {
      const isActive = i === index;
      m.classList.toggle('is-active', isActive);
      m.classList.toggle('is-dimmed', !isActive);
      m.setAttribute('aria-expanded', String(isActive));
    });

    const activeMember = members[index];
    const props = [...activeMember.querySelectorAll<HTMLElement>('.team-prop')];
    const badge = activeMember.querySelector<HTMLElement>('.team-badge');

    // Fade in backdrop
    if (backdrop) {
      gsap.to(backdrop, { opacity: 1, duration: 0.35, ease: 'power2.out' });
    }

    // Burst props out from the centre
    if (props.length) {
      gsap.killTweensOf(props);
      const shuffled = [...props].sort(() => Math.random() - 0.5);

      if (reduce()) {
        gsap.set(props, { opacity: 1, scale: 1 });
        isTransitioning = false;
      } else {
        gsap.fromTo(
          shuffled,
          {
            opacity: 0,
            scale: 0,
            x: 0,
            y: 0,
          },
          {
            opacity: 1,
            scale: 1,
            duration: 0.55,
            stagger: 0.06,
            ease: 'back.out(2)',
            onComplete: () => { isTransitioning = false; },
          }
        );
      }
    } else {
      isTransitioning = false;
    }

    // Reveal badge card
    if (badge) {
      gsap.killTweensOf(badge);
      if (reduce()) {
        gsap.set(badge, { opacity: 1, y: 0, scale: 1 });
      } else {
        gsap.fromTo(
          badge,
          { opacity: 0, y: 24, scale: 0.94 },
          { opacity: 1, y: 0, scale: 1, duration: 0.45, delay: 0.08, ease: 'power3.out' }
        );
      }
    }
  };

  const collapse = () => {
    if (activeIndex === null) return;
    const currentMember = members[activeIndex];
    activeIndex = null;

    root.classList.remove('is-expanded');

    const props = currentMember ? [...currentMember.querySelectorAll<HTMLElement>('.team-prop')] : [];
    const badge = currentMember ? currentMember.querySelector<HTMLElement>('.team-badge') : null;

    if (props.length) {
      gsap.to(props, {
        opacity: 0,
        scale: 0.2,
        duration: 0.24,
        ease: 'power2.in',
      });
    }

    if (badge) {
      gsap.killTweensOf(badge);
      gsap.to(badge, { opacity: 0, y: 12, duration: 0.2, ease: 'power2.in' });
    }
    isTransitioning = false;

    if (backdrop) {
      gsap.to(backdrop, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          members.forEach((m) => {
            m.classList.remove('is-active', 'is-dimmed');
            m.setAttribute('aria-expanded', 'false');
          });
        },
      });
    } else {
      members.forEach((m) => {
        m.classList.remove('is-active', 'is-dimmed');
        m.setAttribute('aria-expanded', 'false');
      });
    }
  };

  // Click & keyboard events on each member
  members.forEach((m, i) => {
    m.addEventListener('click', (e) => {
      e.stopPropagation();
      expandMember(i);
    });

    m.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        expandMember(i);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const next = (i + 1) % members.length;
        members[next].focus();
        if (activeIndex !== null) expandMember(next);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = (i - 1 + members.length) % members.length;
        members[prev].focus();
        if (activeIndex !== null) expandMember(prev);
      }
    });
  });

  // Clicking backdrop collapses
  if (backdrop) {
    backdrop.addEventListener('click', (e) => {
      e.stopPropagation();
      collapse();
    });
  }

  // Clicking outside collapses
  document.addEventListener('click', (e) => {
    if (activeIndex !== null && !root.contains(e.target as Node)) {
      collapse();
    }
  });

  // Escape key collapses
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeIndex !== null) {
      collapse();
    }
  });
}

// ── [data-filter] buttons show only the cards whose data-tags include that value ──
function initFilter(): void {
  const btns = [...document.querySelectorAll<HTMLButtonElement>('[data-filter]')];
  const items = [...document.querySelectorAll<HTMLElement>('[data-tags]')];
  const grid = document.querySelector<HTMLElement>('.work-grid');
  btns.forEach((b) => b.addEventListener('click', () => {
    const f = b.dataset.filter ?? 'all';
    btns.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    items.forEach((it) => { it.hidden = f !== 'all' && !(it.dataset.tags ?? '').split(' ').includes(f); });
    if (grid) {
      if (f !== 'all') grid.setAttribute('data-filtered', 'true');
      else grid.removeAttribute('data-filtered');
    }
    ScrollTrigger.refresh();
  }));
}

// ── video[data-inview]: plays only while on screen (hardware decoder ceiling, Guardrail 1) ──
function initInlineVideos(): void {
  document.querySelectorAll<HTMLVideoElement>('video[data-inview]').forEach((v) => {
    v.muted = true;
    new IntersectionObserver(([e]) => { e.isIntersecting && !reduce() ? v.play().catch(() => {}) : v.pause(); }, { threshold: 0.25 }).observe(v);
  });
}

